import { useState } from "react";
import { Link } from "react-router-dom";
import { useBrochureManager } from "@/hooks/useBrochureManager";
import { healthInsuranceCompanies } from "@/mocks/healthInsuranceCompanies";
import {
  Printer,
  X,
  Download,
  FileText,
  CheckCircle,
  Phone,
  Calendar,
} from "lucide-react";

export default function ComparisonGuideModal() {
  const [isOpen, setIsOpen] = useState(false);
  const { logDownload } = useBrochureManager();

  const handleOpen = () => {
    setIsOpen(true);
    logDownload(
      "Complete Health Insurance Comparison Guide",
      "comparison_guide"
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const allPlans = healthInsuranceCompanies.flatMap((company) =>
    company.plans.map((plan) => ({ ...plan, companyName: company.name }))
  );

  if (!isOpen) {
    return (
      <>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
          <button
            onClick={handleOpen}
            className="inline-flex items-center gap-2 bg-cta-600 hover:bg-cta-700 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            <Printer className="w-4 h-4" />
            Download Complete Comparison Guide
          </button>
          <button
            onClick={() => {
              const allUrls = healthInsuranceCompanies.flatMap((c) =>
                c.plans.map((p) => ({ name: p.name, url: p.brochureUrl }))
              );
              logDownload("All Brochures Bulk", "all_brochures");
              allUrls.forEach((u, i) => {
                setTimeout(() => window.open(u.url, "_blank"), i * 800);
              });
            }}
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 font-semibold text-sm px-5 py-2.5 rounded-full transition-all border border-slate-200 whitespace-nowrap cursor-pointer"
          >
            <Download className="w-4 h-4" />
            Download All Brochures
          </button>
        </div>
        <p className="text-center text-xs text-slate-400 -mt-5 mb-8">
          Get a printable summary of all plans or open every brochure at once.
          Perfect for offline comparison and sharing with family.
        </p>
      </>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-5xl my-8 shadow-2xl overflow-hidden">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 p-4 md:p-5 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-slate-900">
              Health Insurance Comparison Guide
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              ExpertAdvise.in — All Plans Summary · {new Date().toLocaleDateString("en-IN")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="hidden sm:inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors cursor-pointer"
            >
              <Printer className="w-4 h-4" />
              Print / Save PDF
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Print-friendly content */}
        <div className="p-5 md:p-8 print:p-0">
          {/* Cover */}
          <div className="text-center mb-8 pb-8 border-b border-slate-200 print:border-b print:border-slate-300">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-600 to-primary-700 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
              Health Insurance Comparison Guide 2025
            </h1>
            <p className="text-sm text-slate-500 max-w-xl mx-auto">
              A complete side-by-side summary of health insurance plans from
              HDFC ERGO, Niva Bupa, and ICICI Lombard. Use this guide to compare
              coverage, benefits, and find the best plan for your family.
            </p>
            <div className="flex items-center justify-center gap-4 mt-4 text-xs text-slate-400">
              <span>Generated: {new Date().toLocaleDateString("en-IN")}</span>
              <span>·</span>
              <span>{allPlans.length} Plans Compared</span>
              <span>·</span>
              <span>3 Insurers</span>
            </div>
          </div>

          {/* Plans Summary */}
          <div className="space-y-8">
            {healthInsuranceCompanies.map((company) => (
              <div key={company.id}>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className={`w-10 h-10 rounded-lg bg-gradient-to-br ${company.logoColor} flex items-center justify-center`}
                  >
                    <i className={`${company.logoIcon} text-white text-lg`} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">
                      {company.name}
                    </h3>
                    <a
                      href={company.officialBrochurePage}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-cta-600 hover:underline"
                    >
                      View Official Brochures →
                    </a>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {company.plans.map((plan) => (
                    <div
                      key={plan.id}
                      className="bg-slate-50 rounded-xl p-4 border border-slate-100"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-bold text-slate-800">
                          {plan.name}
                        </h4>
                        {plan.specialNotes && (
                          <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-medium border border-amber-100">
                            Diabetes Plan
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mb-3">
                        Best for: {plan.bestSuitedFor}
                      </p>
                      <ul className="space-y-1.5 mb-3">
                        {plan.keyBenefits.slice(0, 4).map((b, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-1.5 text-xs text-slate-600"
                          >
                            <CheckCircle className="w-3 h-3 text-cta-500 shrink-0 mt-0.5" />
                            {b}
                          </li>
                        ))}
                        {plan.keyBenefits.length > 4 && (
                          <li className="text-xs text-slate-400 pl-4.5">
                            +{plan.keyBenefits.length - 4} more benefits
                          </li>
                        )}
                      </ul>
                      <a
                        href={plan.brochureUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700"
                      >
                        <Download className="w-3 h-3" />
                        Download Brochure
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Quick Comparison Table */}
          <div className="mt-10 pt-8 border-t border-slate-200 print:border-t print:border-slate-300">
            <h3 className="text-base font-bold text-slate-900 mb-4">
              Quick Comparison Matrix
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-100 border-b border-slate-200">
                    <th className="text-left px-3 py-2 font-semibold text-slate-700 sticky left-0 bg-slate-100">
                      Plan
                    </th>
                    <th className="text-left px-3 py-2 font-semibold text-slate-700">
                      Insurer
                    </th>
                    <th className="text-left px-3 py-2 font-semibold text-slate-700">
                      Best For
                    </th>
                    <th className="text-left px-3 py-2 font-semibold text-slate-700 hidden sm:table-cell">
                      Special Notes
                    </th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-700">
                      Brochure
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {allPlans.map((plan) => (
                    <tr
                      key={plan.id}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-3 py-2 font-medium text-slate-800 sticky left-0 bg-white">
                        {plan.name}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {plan.companyName}
                      </td>
                      <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate">
                        {plan.bestSuitedFor}
                      </td>
                      <td className="px-3 py-2 text-slate-600 hidden sm:table-cell">
                        {plan.specialNotes ? (
                          <span className="text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                            {plan.specialNotes}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <a
                          href={plan.brochureUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline"
                        >
                          PDF
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-8 p-4 rounded-xl bg-amber-50 border border-amber-100 text-xs text-amber-800 leading-relaxed">
            <strong>Disclaimer:</strong> Brochures and policy details are hosted
            on official insurer websites. Please verify the latest terms,
            conditions, exclusions, and IRDAI-approved documents before purchase.
            Insurance is the subject matter of solicitation. ExpertAdvise.in is an
            advisory platform and not an insurance provider or broker.
          </div>

          {/* CTA Footer */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 print:hidden">
            <Link
              to="/book-appointment?source=Health%20Insurers%20%26%20Brochures%20Page"
              className="inline-flex items-center gap-2 bg-cta-600 hover:bg-cta-700 text-white font-bold text-sm px-6 py-3 rounded-full transition-all shadow-lg"
            >
              <Calendar className="w-4 h-4" />
              Book Free Consultation
            </Link>
            <a
              href="https://wa.me/919999365655?text=Hi%20ExpertAdvise%2C%20I%20need%20health%20insurance%20advice"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold text-sm px-6 py-3 rounded-full transition-all shadow-lg"
            >
              <Phone className="w-4 h-4" />
              WhatsApp an Advisor
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}